import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  alpha,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  VideoCall as VideoCallIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import publicAxios from '../../api/publicAxios';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toastify';
import { savePendingBooking, clearPendingBooking, getPendingBooking, shouldRestoreBookingData } from '../../utils/bookingStorage';

/**
 * Quick Booking Widget - Streamlined booking component for doctor profiles
 * Allows users to quickly book appointments without leaving the profile page
 */
const QuickBookingWidget = ({ doctor, onBookingSuccess }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Date/Time, 2: Details, 3: Confirm
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState(null);
  
  // Patient details
  const [patientName, setPatientName] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [consultationType, setConsultationType] = useState('video');
  const [reason, setReason] = useState('');
  const [clinicId, setClinicId] = useState(null);
  const [clinicName, setClinicName] = useState(null);
  
  // Check if user is logged in
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isLoggedIn = !!user;

  // Pre-fill patient details if logged in, and clinic info if doctor has clinic
  useEffect(() => {
    if (isLoggedIn && user) {
      setPatientName(user.name || '');
      setPatientPhone(user.phone_number || '');
      setPatientEmail(user.email || '');
    }
    
    // Pre-fill clinic information if doctor has associated clinic
    if (doctor) {
      const doctorClinic = doctor.clinic;
      const doctorClinicId = doctor.clinic?.id || doctor.clinic_id || doctor.clinicId;
      
      if (doctorClinicId) {
        setClinicId(doctorClinicId);
        setClinicName(doctorClinic?.name || doctorClinic?.clinic_name || null);
        console.log('Doctor has clinic - pre-filled:', {
          clinicId: doctorClinicId,
          clinicName: doctorClinic?.name || doctorClinic?.clinic_name,
        });
      } else {
        // Clear clinic info if doctor doesn't have one
        setClinicId(null);
        setClinicName(null);
      }
    }
  }, [isLoggedIn, user, doctor]);

  // Reset form state when booking is successful or when starting fresh
  const resetForm = useCallback(() => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setPatientName('');
    setPatientPhone('');
    setPatientEmail('');
    setConsultationType('video');
    setReason('');
    setError(null);
  }, []);

  // Fetch available slots when date is selected
  useEffect(() => {
    const doctorId = doctor?.id || doctor?.uuid || doctor?.doctor_id;
    if (selectedDate && doctorId) {
      fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, doctor?.id, doctor?.uuid, doctor?.doctor_id]);

  const fetchAvailableSlots = async () => {
    if (!selectedDate || (!doctor?.id && !doctor?.uuid && !doctor?.doctor_id)) return;
    
    setLoadingSlots(true);
    setError(null);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const doctorId = doctor.id || doctor.uuid || doctor.doctor_id;
      const response = await publicAxios.get(
        `/doctors/${doctorId}/availability/?date=${dateStr}`
      );
      
      // Transform availability data into time slots
      // Handle different response formats
      const data = response.data || {};
      let slots = [];
      
      if (Array.isArray(data.available_slots)) {
        slots = data.available_slots;
      } else if (Array.isArray(data.slots)) {
        slots = data.slots;
      } else if (data.availability && Array.isArray(data.availability)) {
        slots = data.availability.map(item => item.time || item.slot_time || item);
      } else if (data.time_slots && Array.isArray(data.time_slots)) {
        slots = data.time_slots;
      }
      
      // Ensure slots are strings (time format)
      slots = slots.map(slot => {
        if (typeof slot === 'string') return slot;
        if (slot.time) return slot.time;
        if (slot.slot_time) return typeof slot.slot_time === 'string' ? slot.slot_time : slot.slot_time.toString();
        return String(slot);
      });
      
      setAvailableSlots(slots);
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError(err.response?.data?.error || 'Failed to load available time slots. Please try another date.');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleOpen = () => {
    const doctorId = doctor?.id || doctor?.uuid || doctor?.doctor_id;
    
    // Check if there's old booking data for a different doctor
    const shouldRestore = shouldRestoreBookingData(doctorId);
    
    // Clear old booking data if it's for a different doctor
    if (!shouldRestore && doctorId) {
      clearPendingBooking();
      // Reset all fields for fresh booking
      resetForm();
    }
    
    // Try to restore pending booking if it's for this doctor
    const pendingBooking = getPendingBooking();
    if (pendingBooking && (pendingBooking.doctor_id === doctorId || pendingBooking.doctorId === doctorId)) {
      // Restore pending booking data
      if (pendingBooking.selectedDate) {
        try {
          const restoredDate = new Date(pendingBooking.selectedDate);
          if (!isNaN(restoredDate.getTime())) {
            setSelectedDate(restoredDate);
          }
        } catch (e) {
          console.warn('Failed to restore date:', e);
        }
      }
      if (pendingBooking.selectedTime) {
        setSelectedTime(pendingBooking.selectedTime);
      }
      if (pendingBooking.consultationType) {
        setConsultationType(pendingBooking.consultationType);
      }
      if (pendingBooking.appointmentData) {
        if (pendingBooking.appointmentData.patient_name) {
          setPatientName(pendingBooking.appointmentData.patient_name);
        }
        if (pendingBooking.appointmentData.patient_phone) {
          setPatientPhone(pendingBooking.appointmentData.patient_phone);
        }
        if (pendingBooking.appointmentData.patient_email) {
          setPatientEmail(pendingBooking.appointmentData.patient_email);
        }
        if (pendingBooking.appointmentData.details) {
          setReason(pendingBooking.appointmentData.details);
        }
      }
      // Advance to appropriate step
      if (pendingBooking.selectedDate && pendingBooking.selectedTime) {
        if (pendingBooking.appointmentData?.patient_name && pendingBooking.appointmentData?.patient_phone) {
          setStep(3); // Confirmation step
        } else {
          setStep(2); // Patient details step
        }
      }
    }
    
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    // Don't reset step or clear data on close - user might reopen
    // Only clear error
    setError(null);
  };

  // Clear booking data when component unmounts or doctor changes
  useEffect(() => {
    const doctorId = doctor?.id || doctor?.uuid || doctor?.doctor_id;
    return () => {
      // Cleanup: clear pending booking if it's not for current doctor
      const pendingBooking = getPendingBooking();
      if (pendingBooking && pendingBooking.doctor_id !== doctorId && pendingBooking.doctorId !== doctorId) {
        clearPendingBooking();
      }
    };
  }, [doctor?.id, doctor?.uuid, doctor?.doctor_id]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null); // Reset time when date changes
  };

  const handleNext = () => {
    if (step === 1) {
      if (!selectedDate || !selectedTime) {
        setError('Please select a date and time');
        return;
      }
      setError(null);
      setStep(2);
    } else if (step === 2) {
      // Validate patient details
      if (!patientName.trim()) {
        setError('Please enter your name');
        return;
      }
      if (!patientPhone.trim()) {
        setError('Please enter your phone number');
        return;
      }
      if (!consultationType) {
        setError('Please select a consultation type');
        return;
      }
      setError(null);
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setError(null);
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select date and time');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const appointmentData = {
        scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
        scheduled_time: selectedTime,
        type: consultationType === 'video' ? 'Video' : consultationType === 'phone' ? 'Phone' : 'In-Person',
        details: reason,
        patient_name: patientName,
        patient_phone: patientPhone,
        patient_email: patientEmail || undefined,
      };

      // IMPORTANT: Booking requires authentication - check first
      if (!isLoggedIn) {
        // Save booking data to localStorage for after login
        const doctorId = doctor.id || doctor.uuid || doctor.doctor_id;
        const bookingData = {
          doctor_id: doctorId,
          doctorId, // Store both for compatibility
          doctor_name: doctor.name || doctor.full_name,
          appointmentData,
          selectedDate: format(selectedDate, 'yyyy-MM-dd'),
          selectedTime,
          consultationType,
          clinic_id: doctor.clinic?.id || doctor.clinic_id || doctor.clinicId || null,
          clinic_name: doctor.clinic?.name || doctor.clinic?.clinic_name || null,
        };
        
        // Use utility function to save with timestamp
        savePendingBooking(bookingData);
        
        handleClose();
        
        toast.info('Please sign in to book your appointment', {
          position: 'top-center',
          autoClose: 3000,
        });
        
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Please sign in to book your appointment',
              redirectTo: `/doctor/${doctor.id || doctor.uuid || doctor.doctor_id}`
            } 
          });
        }, 500);
        return;
      }

      // User is logged in - proceed with booking
      // Handle both UUID and integer doctor IDs
      const doctorId = doctor.id || doctor.uuid || doctor.doctor_id;
      
      const response = await axiosInstance.post(
        `/doctors/${doctorId}/book/`,
        {
          scheduled_date: appointmentData.scheduled_date,
          scheduled_time: appointmentData.scheduled_time,
          type: appointmentData.type,
          details: appointmentData.details,
          // Include clinic ID if doctor has associated clinic
          ...(clinicId && { clinic_id: clinicId }),
          // Remove patient details - backend uses authenticated user
        }
      );

      // Success
      toast.success('Appointment booked successfully!', {
        position: 'top-center',
        autoClose: 3000,
      });
      
      // Clear any pending booking data
      clearPendingBooking();
      
      // Reset form for next booking
      resetForm();
      
      if (onBookingSuccess) {
        onBookingSuccess(response.data);
      }
      
      handleClose();
      
      // Redirect to appointments page for logged-in users
      setTimeout(() => {
        navigate('/patient/appointments');
      }, 1500);
    } catch (err) {
      console.error('Booking error:', err);
      const errorMessage = err.response?.data?.error 
        || err.response?.data?.message 
        || err.message 
        || 'Failed to book appointment. Please try again.';
      
      setError(errorMessage);
      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card
        sx={{
          position: 'sticky',
          top: 20,
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Stack spacing={3}>
            {/* Price */}
            <Box>
              <Typography variant="h4" fontWeight={700} color="primary">
                ₹{doctor?.consultation_fee || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                per consultation
              </Typography>
            </Box>

            <Divider />

            {/* Consultation Types */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Available for:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {doctor?.consultation_types?.map((type, index) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    icon={type.toLowerCase().includes('video') ? <VideoCallIcon /> : <PhoneIcon />}
                  />
                ))}
                {(!doctor?.consultation_types || doctor.consultation_types.length === 0) && (
                  <Chip label="Video, Phone, In-Person" size="small" />
                )}
              </Stack>
            </Box>

            {/* Quick Book Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<CalendarIcon />}
              onClick={handleOpen}
              sx={{
                py: 1.5,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            >
              Book Appointment
            </Button>

            {/* Next Available */}
            {doctor?.next_available && (
              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Next available: {doctor.next_available.date}
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Booking Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Book Appointment with {doctor?.title || `Dr. ${doctor?.name || doctor?.full_name || 'Doctor'}`}
            </Typography>
            <IconButton onClick={handleClose} size="small" disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Step 1: Date & Time Selection */}
            {step === 1 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Select Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Choose a date"
                      value={selectedDate}
                      onChange={handleDateSelect}
                      minDate={new Date()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: 'Select a date to see available time slots',
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                {selectedDate && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Select Time
                    </Typography>
                    {loadingSlots ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : availableSlots.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {availableSlots.map((slot, index) => (
                          <Button
                            key={index}
                            variant={selectedTime === slot ? 'contained' : 'outlined'}
                            onClick={() => setSelectedTime(slot)}
                            startIcon={<TimeIcon />}
                            sx={{ minWidth: 100 }}
                          >
                            {slot}
                          </Button>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">
                        No available slots for this date. Please select another date.
                      </Alert>
                    )}
                  </Box>
                )}
              </Stack>
            )}

            {/* Step 2: Patient Details */}
            {step === 2 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Consultation Type *
                  </Typography>
                  <FormControl fullWidth error={!consultationType}>
                    <RadioGroup
                      value={consultationType || 'video'}
                      onChange={(e) => {
                        const newType = e.target.value;
                        if (newType) {
                          setConsultationType(newType);
                        }
                      }}
                      row
                    >
                      <FormControlLabel 
                        value="video" 
                        control={<Radio />} 
                        label="Video Call"
                        disabled={loading}
                      />
                      <FormControlLabel 
                        value="phone" 
                        control={<Radio />} 
                        label="Phone Call"
                        disabled={loading}
                      />
                      <FormControlLabel 
                        value="in-person" 
                        control={<Radio />} 
                        label="In-Person"
                        disabled={loading}
                      />
                    </RadioGroup>
                    {!consultationType && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        Please select a consultation type
                      </Typography>
                    )}
                  </FormControl>
                </Box>

                <TextField
                  fullWidth
                  label="Your Name *"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={patientPhone}
                  onChange={(e) => setPatientPhone(e.target.value)}
                  required
                  type="tel"
                />

                <TextField
                  fullWidth
                  label="Email (Optional)"
                  value={patientEmail}
                  onChange={(e) => setPatientEmail(e.target.value)}
                  type="email"
                />

                <TextField
                  fullWidth
                  label="Reason for Visit (Optional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Briefly describe why you're booking this appointment..."
                />
              </Stack>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <Stack spacing={2}>
                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Appointment Summary
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Date:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedDate && format(selectedDate, 'EEEE, MMMM dd, yyyy')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Time:</Typography>
                      <Typography variant="body2" fontWeight={500}>{selectedTime}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Type:</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                        {consultationType.replace('-', ' ')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Fee:</Typography>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        ₹{doctor?.consultation_fee || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {!isLoggedIn && (
                  <Alert severity="info">
                    You'll be asked to sign in after booking to confirm your appointment.
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            {step > 1 && (
              <Button onClick={handleBack} disabled={loading}>
                Back
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} disabled={loading} color="inherit">
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || (step === 1 && (!selectedDate || !selectedTime)) || (step === 2 && (!patientName.trim() || !patientPhone.trim()))}
                sx={{ minWidth: 100 }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleBooking}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                sx={{ minWidth: 140 }}
              >
                {loading ? 'Booking...' : 'Confirm Booking'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickBookingWidget;

